import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FinalCTA = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setStatus('error');
      setMessage('Please enter your name');
      return;
    }
    
    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');

    try {
      const response = await axios.post(`${API}/waitlist`, {
        email,
        name,
        source: 'final_cta'
      });

      if (response.data.success) {
        setStatus('success');
        setMessage(response.data.message);
        setName('');
        setEmail('');
      } else {
        setStatus('error');
        setMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      if (error.response?.data?.detail) {
        setMessage(typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Invalid email format');
      } else {
        setMessage('Something went wrong. Please try again.');
      }
    }
  };

  return (
    <section id="final-cta" className="py-[35px] md:py-24 bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-600 relative overflow-hidden" data-testid="final-cta-section">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-pink-500/20 rounded-full blur-3xl" />
      </div>

      <div className="max-w-3xl mx-auto px-5 md:px-8 relative z-10 text-center">
        {/* Header */}
        <motion.h2 
          className="text-3xl md:text-4xl font-bold text-white mb-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          Scale Your Brand Without<br />Scaling Your Team
        </motion.h2>
        
        <motion.p 
          className="text-lg text-white/80 mb-8 max-w-2xl mx-auto px-4 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          Join early access and be among the first to automate<br className="hidden md:inline" />{' '}your brand distribution.
        </motion.p>

        {/* Form */}
        <motion.div
          className="max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <AnimatePresence mode="wait">
            {status === 'success' ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center justify-center gap-3 py-4 px-6 bg-white/20 backdrop-blur-sm rounded-2xl"
              >
                <CheckCircle2 className="w-5 h-5 text-emerald-300" />
                <span className="text-white font-medium">{message}</span>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={handleSubmit}
                className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-4 w-full px-4 md:px-0"
              >
                <input
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (status === 'error') setStatus('idle');
                  }}
                  placeholder="Enter your name"
                  className="w-full md:w-auto md:flex-1 px-5 py-4 rounded-xl bg-white/15 backdrop-blur-sm border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/30"
                  disabled={status === 'loading'}
                  data-testid="final-cta-name"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (status === 'error') setStatus('idle');
                  }}
                  placeholder="Enter your email"
                  className="w-full md:w-auto md:flex-1 px-5 py-4 rounded-xl bg-white/15 backdrop-blur-sm border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/30"
                  disabled={status === 'loading'}
                  data-testid="final-cta-email"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full md:w-auto md:flex-none h-14 px-8 rounded-xl bg-white text-indigo-600 font-semibold hover:bg-white/90 transition-colors duration-200 flex items-center justify-center gap-2 whitespace-nowrap"
                  data-testid="final-cta-submit"
                >
                  {status === 'loading' ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Join the Waitlist
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
          
          {status === 'error' && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-red-200 text-sm mt-3"
            >
              {message}
            </motion.p>
          )}
        </motion.div>

        {/* Trust indicators */}
        <motion.div
          className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm text-white/60"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
        >
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4 text-emerald-300" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Free early access
          </span>
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4 text-emerald-300" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            No credit card
          </span>
        </motion.div>
      </div>
    </section>
  );
};

export default FinalCTA;
